<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';
import LabelActions from './LabelActions.vue';
import CustomAttributeActions from './CustomAttributeActions.vue';
import DeleteConfirmationModal from './DeleteConfirmationModal.vue';

const props = defineProps({
  allContactsSelected: {
    type: Boolean,
    default: false,
  },
  selectedContacts: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'selectAllContacts',
  'deleteContacts',
  'assignLabels',
  'addCustomAttributes',
]);

const { t } = useI18n();

const showLabelActions = ref(false);
const showCustomAttributeActions = ref(false);
const showDeleteModal = ref(false);

const selectAll = () => {
  // Toggle the selection state
  const newState = !props.allContactsSelected;
  emit('selectAllContacts', newState);
};

const toggleLabelActions = () => {
  showLabelActions.value = !showLabelActions.value;
};

const toggleCustomAttributeActions = () => {
  showCustomAttributeActions.value = !showCustomAttributeActions.value;
};

const showDeleteConfirmation = () => {
  showDeleteModal.value = true;
};

const hideDeleteModal = () => {
  showDeleteModal.value = false;
};

const confirmDelete = () => {
  emit('deleteContacts', props.selectedContacts);
  hideDeleteModal();
};

const assignLabels = labels => {
  emit('assignLabels', labels);
  showLabelActions.value = false;
};

const addCustomAttributes = attributes => {
  emit('addCustomAttributes', attributes);
  showCustomAttributeActions.value = false;
};
</script>

<template>
  <div class="bulk-action__container">
    <div class="flex items-center justify-between">
      <label class="flex items-center justify-between bulk-action__panel">
        <input
          type="checkbox"
          class="checkbox"
          :checked="allContactsSelected"
          :indeterminate="selectedContacts.length > 0 && !allContactsSelected"
          @click="selectAll"
        />
        <span>
          {{
            selectedContacts.length > 0
              ? t('BULK_ACTION.CONTACTS_SELECTED', {
                  contactCount: selectedContacts.length,
                })
              : t('BULK_ACTION.SELECT_ALL_CONTACTS')
          }}
        </span>
      </label>
      <div
        v-if="selectedContacts.length > 0"
        class="flex items-center gap-1 bulk-action__actions"
      >
        <NextButton
          v-tooltip="t('BULK_ACTION.LABELS.ASSIGN_LABELS')"
          icon="i-lucide-tags"
          slate
          xs
          faded
          @click="toggleLabelActions"
        />
        <NextButton
          v-tooltip="
            t('BULK_ACTION.CUSTOM_ATTRIBUTES.UPDATE_CUSTOM_ATTRIBUTES')
          "
          icon="i-lucide-settings"
          slate
          xs
          faded
          @click="toggleCustomAttributeActions"
        />
        <NextButton
          v-tooltip="t('BULK_ACTION.DELETE.DELETE_CONTACTS')"
          icon="i-lucide-trash-2"
          slate
          xs
          faded
          @click="showDeleteConfirmation"
        />
      </div>
      <transition name="popover-animation">
        <LabelActions
          v-if="showLabelActions"
          class="label-actions-box"
          :selected-count="selectedContacts.length"
          @assign="assignLabels"
          @close="showLabelActions = false"
        />
      </transition>
      <transition name="popover-animation">
        <CustomAttributeActions
          v-if="showCustomAttributeActions"
          class="custom-attribute-actions-box"
          :selected-count="selectedContacts.length"
          @add="addCustomAttributes"
          @close="showCustomAttributeActions = false"
        />
      </transition>
    </div>
    <div v-if="allContactsSelected" class="bulk-action__alert">
      {{ t('BULK_ACTION.ALL_CONTACTS_SELECTED_ALERT') }}
    </div>
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :selected-count="selectedContacts.length"
      @confirm="confirmDelete"
      @close="hideDeleteModal"
    />
  </div>
</template>

<style scoped lang="scss">
.bulk-action__container {
  @apply p-4 relative border-b border-solid border-n-strong dark:border-n-weak;
}

.bulk-action__panel {
  @apply cursor-pointer;

  span {
    @apply text-xs my-0 mx-1;
  }

  input[type='checkbox'] {
    @apply cursor-pointer m-0;
  }
}

.bulk-action__alert {
  @apply bg-n-amber-3 text-n-amber-12 rounded text-xs mt-2 py-1 px-2 border border-solid border-n-amber-5;
}

.popover-animation-enter-active,
.popover-animation-leave-active {
  transition: transform ease-out 0.1s;
}

.popover-animation-enter {
  transform: scale(0.95);
  @apply opacity-0;
}

.popover-animation-enter-to {
  transform: scale(1);
  @apply opacity-100;
}

.popover-animation-leave {
  transform: scale(1);
  @apply opacity-100;
}

.popover-animation-leave-to {
  transform: scale(0.95);
  @apply opacity-0;
}

.label-actions-box {
  --triangle-position: 5.3125rem;
}
.custom-attribute-actions-box {
  --triangle-position: 3.5rem;
}
</style>
